import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { ChevronLeftIcon } from '@heroicons/react/outline';
import { DotsVerticalIcon } from '@heroicons/react/solid';
import { Affix, Layout, Tooltip } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import LeftRotateIcon from 'assets/Icons/rotateIcon/LeftRotateIcon';
import RightRotateIcon from 'assets/Icons/rotateIcon/RightRotateIcon';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { UAParser } from 'ua-parser-js';

import { aiModalOpenAtom } from '@/atoms/pdfStates/aiModalOpenAtom';
import { drawerPdfAtom } from '@/atoms/pdfStates/drawerAtom';
import { iframeModalAtom } from '@/atoms/pdfStates/iframeModalAtom';
import { mobileToolboxVisibleAtom } from '@/atoms/pdfStates/mobileToolboxVisible';
import { getBookByIdApi } from '@/services/Library/Books';

import { ShouldRender } from '../shared/basic/ShouldRender';
import { isBannerVisible } from '../../atoms/isBannerVisible';
import DoublePageIcon from '../../../assets/Icons/DoublePageIcon';
import SinglePageIcon from '../../../assets/Icons/SinglePageIcon';

const { Header } = Layout;

interface Props {
  isFullScreen: boolean;
  toggleFullScreen: () => void;
  zoomValue: number;
  zoomOut: () => void;
  zoomIn: () => void;
  sideBySide: boolean;
  title: string;
  rotateLeft: (pageNumber: number) => void;
  pageNumber: number;
  rotateRight: (pageNumber: number) => void;
  toggleSideBySide: () => void;
  openDrawer: boolean;
  isModal?: boolean;
  handleCancel?: () => void;
  headerClass?: string;
  isIFrame?: boolean;
  fullScreenIcon?: React.ReactNode;
  hardcoverProductId?: number | string;
  isHardCoverAvailable?: boolean;
}

const PdfReaderHeader: React.FC<Props> = ({
  isFullScreen,
  toggleFullScreen,
  zoomValue,
  zoomOut,
  zoomIn,
  sideBySide,
  title,
  rotateLeft,
  pageNumber,
  rotateRight,
  toggleSideBySide,
  openDrawer,
  headerClass,
  isModal = false,
  isIFrame = false,
  fullScreenIcon,
  hardcoverProductId,
  isHardCoverAvailable,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [, setMobileToolboxVisible] = useRecoilState(mobileToolboxVisibleAtom);
  const [, setDrawerState] = useRecoilState(drawerPdfAtom);
  const router = useRouter();
  const screens = useBreakpoint();
  const isBannerVisibleValue = useRecoilValue(isBannerVisible);
  const [, setOpenDrawer] = useRecoilState<boolean>(aiModalOpenAtom);
  const [, setIsModalVisible] = useRecoilState(iframeModalAtom);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 56);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isIphone = (): boolean => {
    const parser = new UAParser();
    const device = parser.getDevice();
    return device.model === 'iPhone';
  };

  const handleUpgrade = async () => {
    if (isFullScreen) toggleFullScreen();
    let hardId = hardcoverProductId;
    if (!hardId) {
      const bookId =
        (router.query.book_id as string) ||
        router.asPath.split('/')[2]?.replace(/\?.*/, '');
      if (bookId) {
        try {
          const res = await getBookByIdApi(bookId);
          hardId =
            res?.hardcoverProductId ||
            res?.hardCoverProductId ||
            res?.productId;
        } catch (e) {
          console.error('Error fetching book details for upgrade:', e);
        }
      }
    }

    const targetUrl = hardId
      ? `/checkout/upgrad-to-hardcopy?productId=${hardId}`
      : '/checkout/upgrad-to-hardcopy';

    if (isIFrame) {
      window.open(targetUrl, '_blank');
    } else {
      router.push({
        pathname: '/checkout/upgrad-to-hardcopy',
        ...(hardId ? { query: { productId: hardId } } : {}),
      });
    }
  };

  return (
    <Affix offsetTop={0}>
      <Header
        style={{ height: '56px', lineHeight: 'normal' }}
        className={clsx(
          '!h-14 leading-normal flex items-center transition-all',
          isFullScreen
            ? 'fixed z-20 top-0 left-0 right-0 lg:right-[94px]'
            : 'w-full',
          headerClass ??
            'bg-bgColor bg-opacity-75 !px-3 sm:!px-5 lg:!px-8 backdrop-blur-md border-bgColor border-t',
          !isScrolled
            ? 'mt-0'
            : isBannerVisibleValue && !screens.md
              ? ''
              : 'mt-0',
        )}
      >
        <div className='flex justify-between items-center h-full w-full text-fadedWhite'>
          {/* Left section: back chevron + (title on desktop) + (zoom buttons on mobile) */}
          <div className='flex items-center gap-x-2 md:gap-x-4 shrink-0'>
            <ShouldRender check={!isIFrame}>
              <div
                className='group flex items-center cursor-pointer md:gap-x-3'
                onClick={() => {
                  isFullScreen && toggleFullScreen();
                  setOpenDrawer(false);
                  setDrawerState({
                    contentDrawer: false,
                    notesDrawer: false,
                  });
                  if (isIFrame || isModal) setIsModalVisible(false);
                  else router.push('/books');
                }}
              >
                <div className='flex items-center justify-center md:bg-cartBgColor group-hover:bg-gray-200 group-hover:bg-opacity-15 p-1 w-7 h-7 md:w-6 md:h-6 rounded-full'>
                  <ChevronLeftIcon className='w-5 md:w-4' />
                </div>
                <ShouldRender check={screens.md}>
                  <span className='truncate w-[20vw]'>{title}</span>
                  <span className='border h-1/3 border-backGroundReaderTabBg' />
                </ShouldRender>
              </div>
            </ShouldRender>
            <ShouldRender check={isIFrame}>
              <span className='truncate font-bold text-white w-[30vw] md:w-[20vw]'>
                {title}
              </span>
            </ShouldRender>

            {/* Mobile zoom controls next to divider as seen in phone screenshot */}
            <ShouldRender check={!screens.md}>
              <span className='border-r h-3.5 border-[#3a3a3a] mx-0.5' />
              <div className='flex items-center gap-x-2 sm:gap-x-3'>
                <button
                  type='button'
                  onClick={zoomOut}
                  className='text-white flex items-center justify-center active:opacity-70 p-0.5 w-6 h-6'
                  aria-label='Zoom Out'
                >
                  <ZoomOutOutlined style={{ fontSize: 16 }} />
                </button>
                <button
                  type='button'
                  onClick={zoomIn}
                  className='text-white flex items-center justify-center active:opacity-70 p-0.5 w-6 h-6'
                  aria-label='Zoom In'
                >
                  <ZoomInOutlined style={{ fontSize: 16 }} />
                </button>
              </div>
            </ShouldRender>
          </div>

          {/* Center section: zoom & display controls for tablet/desktop */}
          <ShouldRender check={screens.md}>
            <div
              className={clsx(
                isIphone() && isIFrame
                  ? 'right-[5vw]'
                  : 'left-1/2 -translate-x-1/2',
                'absolute top-1/2 transform left-1/2 -translate-x-1/2 mt-1 md:mt-0 -translate-y-1/2',
              )}
            >
              <div className='flex items-center gap-x-4'>
                <Tooltip title={`Zoom Out ${zoomValue}%`} placement='bottom'>
                  <button onClick={zoomOut} className='text-white'>
                    <ZoomOutOutlined style={{ fontSize: 20 }} />
                  </button>
                </Tooltip>

                <Tooltip title={`Zoom In ${zoomValue}%`} placement='bottom'>
                  <button onClick={zoomIn} className='text-white'>
                    <ZoomInOutlined style={{ fontSize: 20 }} />
                  </button>
                </Tooltip>

                <span className='border h-4 border-backGroundReaderTabBg' />

                <ShouldRender check={!openDrawer}>
                  <motion.button
                    onClick={toggleSideBySide}
                    aria-pressed={sideBySide}
                    className='relative flex items-center w-16 h-8 rounded-full cursor-pointer px-1 bg-[#1C1C1C]'
                    layout
                  >
                    <motion.div
                      className='absolute left-[12px] z-20'
                      animate={{ color: sideBySide ? '#FFFFFF99' : '#1C1C1C' }}
                    >
                      <SinglePageIcon />
                    </motion.div>
                    <motion.div
                      className='absolute right-1.5 z-20'
                      animate={{ color: sideBySide ? '#1C1C1C' : '#FFFFFF99' }}
                    >
                      <DoublePageIcon />
                    </motion.div>
                    <motion.div
                      className='w-7 h-7 rounded-full z-10'
                      layout
                      animate={{
                        x: sideBySide ? 32 : 0,
                        backgroundColor: sideBySide ? '#FFFFFF' : '#F0F0F0',
                        boxShadow: sideBySide
                          ? '0px 0px 12px rgba(0, 102, 255, 0.5)'
                          : '0px 0px 6px rgba(0, 0, 0, 0.1)',
                      }}
                      transition={{ duration: 0.4 }}
                    />
                  </motion.button>
                </ShouldRender>

                <ShouldRender check={!sideBySide}>
                  <span className='border h-4 border-backGroundReaderTabBg mx-4' />
                  <div className='flex gap-x-2'>
                    <Tooltip title='Rotate Anti-Clockwise' placement='bottom'>
                      <button
                        onClick={() => rotateLeft(pageNumber)}
                        className='flex justify-center items-center p-2 w-10 h-10 rounded-full hover:bg-gray-200 hover:bg-opacity-15'
                      >
                        <LeftRotateIcon />
                      </button>
                    </Tooltip>
                    <Tooltip title='Rotate Clockwise' placement='bottom'>
                      <button
                        onClick={() => rotateRight(pageNumber)}
                        className='flex justify-center items-center p-2 w-10 h-10 rounded-full hover:bg-gray-200 hover:bg-opacity-15'
                      >
                        <RightRotateIcon />
                      </button>
                    </Tooltip>
                  </div>
                </ShouldRender>
              </div>
            </div>
          </ShouldRender>

          {/* Right section: Upgrade to hardcopy button + mobile menu */}
          <div className='flex items-center gap-x-1.5 sm:gap-x-3 shrink-0'>
            <ShouldRender check={isHardCoverAvailable !== false}>
              <button
                type='button'
                onClick={handleUpgrade}
                className='h-[26px] sm:h-8 flex items-center justify-center bg-[#EDEDF0] hover:bg-white text-black active:scale-95 transition-all text-[10px] sm:text-xs font-semibold px-2 sm:px-3.5 rounded sm:rounded-lg uppercase tracking-normal sm:tracking-wide whitespace-nowrap cursor-pointer shadow-sm leading-none shrink-0'
              >
                UPGRADE TO HARDCOPY
              </button>
            </ShouldRender>

            <ShouldRender check={!screens.lg}>
              {isIFrame ? (
                <button onClick={toggleFullScreen} className='text-white p-1'>
                  {fullScreenIcon}
                </button>
              ) : (
                <div
                  onClick={() => setMobileToolboxVisible(true)}
                  className='w-7 h-7 flex items-center justify-center text-white hover:text-gray-200 transition-colors cursor-pointer rounded-full active:bg-white/10'
                  aria-label='More options'
                >
                  <DotsVerticalIcon className='w-5 h-5 sm:w-6 sm:h-6' />
                </div>
              )}
            </ShouldRender>
          </div>
        </div>
      </Header>
    </Affix>
  );
};

export default PdfReaderHeader;
